/**
 * Language and unit PREFERENCES — device-bound, like the other display
 * settings (`feedbackSettings.ts`): a phone and a desktop browser of the
 * same user may want different units, and a toggle must not write the
 * `users` document.
 *
 * Import-safe on the server: nothing touches `localStorage` or `navigator`
 * until a function is called, and every access is guarded.
 */

import {
  isLocale,
  type LanguagePreference,
  type UnitPreference,
} from './locale';

const STORAGE_KEY = 'grundo.locale';

export interface LocaleSettings {
  language: LanguagePreference;
  units: UnitPreference;
}

export const DEFAULT_LOCALE_SETTINGS: LocaleSettings = { language: 'auto', units: 'auto' };

/** Field by field: a corrupt `units` must not reset a valid `language`. */
export function normalizeLocaleSettings(raw: unknown): LocaleSettings {
  if (typeof raw !== 'object' || raw === null) return { ...DEFAULT_LOCALE_SETTINGS };
  const source = raw as Record<string, unknown>;
  const language = source.language === 'auto' || isLocale(source.language) ? source.language : 'auto';
  const units =
    source.units === 'auto' || source.units === 'metric' || source.units === 'imperial'
      ? source.units
      : 'auto';
  return { language, units };
}

function read(): LocaleSettings {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (raw === null || raw === undefined) return { ...DEFAULT_LOCALE_SETTINGS };
    return normalizeLocaleSettings(JSON.parse(raw));
  } catch {
    // Private browsing, blocked storage, corrupt JSON: the defaults are right.
    return { ...DEFAULT_LOCALE_SETTINGS };
  }
}

/** The device's preferred languages, best first. Empty on the server. */
export function deviceLanguageTags(): readonly string[] {
  try {
    const nav = globalThis.navigator;
    if (!nav) return [];
    if (Array.isArray(nav.languages) && nav.languages.length > 0) return nav.languages;
    return typeof nav.language === 'string' && nav.language ? [nav.language] : [];
  } catch {
    return [];
  }
}

let current: LocaleSettings | null = null;
const listeners = new Set<() => void>();

/** Stable reference until a real change — `useSyncExternalStore` compares with `Object.is`. */
export function localeSettings(): LocaleSettings {
  if (current === null) current = read();
  return current;
}

export function subscribeToLocaleSettings(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function updateLocaleSettings(patch: Partial<LocaleSettings>): LocaleSettings {
  const next = normalizeLocaleSettings({ ...localeSettings(), ...patch });
  current = next;
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* fine — the choice lives on for this session */
  }
  for (const listener of listeners) listener();
  return next;
}

/** Tests only: drop the in-memory snapshot. */
export function resetLocaleSettingsCache(): void {
  current = null;
}
